<template>
  <button
    class="st-btn"
    :class="[
      `st-btn--${variant}`,
      `st-btn--${size}`,
      { 'st-btn--block': block, 'st-btn--on-shell': onShell, 'st-btn--icon-only': iconOnly },
    ]"
    :type="type"
    :disabled="disabled || loading"
  >
    <font-awesome-icon v-if="loading" :icon="['fas', 'circle']" class="st-btn__spinner" />
    <font-awesome-icon v-else-if="icon" :icon="['fas', icon]" class="st-btn__icon" />
    <slot v-else name="icon" />
    <span v-if="$slots.default" class="st-btn__label"><slot /></span>
  </button>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    /** Visual intent. */
    variant?: 'primary' | 'secondary' | 'ghost' | 'subtle' | 'danger';
    size?: 'sm' | 'md';
    /** Font Awesome solid icon name to show before the label. */
    icon?: string;
    /** Stretch to fill the container width. */
    block?: boolean;
    /** Brighten borders/text so the button reads on the dark shell. */
    onShell?: boolean;
    loading?: boolean;
    disabled?: boolean;
    iconOnly?: boolean;
    type?: 'button' | 'submit' | 'reset';
  }>(),
  { variant: 'secondary', size: 'md', type: 'button' },
);
</script>

<style scoped>
.st-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--st-space-2);
  height: var(--st-control-h-md);
  padding: 0 var(--st-space-4);
  border: 1px solid transparent;
  border-radius: var(--st-radius-sm);
  font-size: var(--st-text-sm);
  font-weight: var(--st-weight-medium);
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: var(--st-transition);
  user-select: none;
}
.st-btn--sm {
  height: var(--st-control-h-sm);
  padding: 0 var(--st-space-3);
  font-size: var(--st-text-xs);
}
.st-btn--block {
  display: flex;
  width: 100%;
}
.st-btn--icon-only {
  padding: 0;
  width: var(--st-control-h-md);
}
.st-btn--icon-only.st-btn--sm {
  width: var(--st-control-h-sm);
}
.st-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ---- primary ---- */
.st-btn--primary {
  background: var(--st-accent);
  color: var(--st-accent-text);
}
.st-btn--primary:hover:not(:disabled) {
  background: var(--st-accent-hover);
}
.st-btn--primary:active:not(:disabled) {
  background: var(--st-accent-active);
}

/* ---- secondary ---- */
.st-btn--secondary {
  background: var(--st-surface);
  border-color: var(--st-border-strong);
  color: var(--st-text);
}
.st-btn--secondary:hover:not(:disabled) {
  background: var(--st-surface-hover);
  border-color: var(--st-graphite-300);
}

/* ---- ghost (transparent, bordered on hover) ---- */
.st-btn--ghost {
  background: transparent;
  color: var(--st-text-muted);
}
.st-btn--ghost:hover:not(:disabled) {
  background: var(--st-surface-hover);
  color: var(--st-text);
}

/* ---- subtle (filled neutral, no border) ---- */
.st-btn--subtle {
  background: var(--st-surface-sunken);
  color: var(--st-text);
}
.st-btn--subtle:hover:not(:disabled) {
  background: var(--st-surface-active);
}

/* ---- danger ---- */
.st-btn--danger {
  background: var(--st-danger);
  color: var(--st-danger-text);
}
.st-btn--danger:hover:not(:disabled) {
  background: var(--st-danger-hover);
}

/* ---- on the dark shell ---- */
.st-btn--on-shell.st-btn--secondary {
  background: var(--st-shell-surface);
  border-color: var(--st-shell-border);
  color: var(--st-shell-text);
}
.st-btn--on-shell.st-btn--secondary:hover:not(:disabled) {
  background: var(--st-shell-surface-hover);
  border-color: var(--st-shell-border-strong);
}
.st-btn--on-shell.st-btn--ghost {
  color: var(--st-shell-text-muted);
}
.st-btn--on-shell.st-btn--ghost:hover:not(:disabled) {
  background: var(--st-shell-surface);
  color: var(--st-shell-text);
}
.st-btn--on-shell.st-btn--danger {
  background: transparent;
  border-color: var(--st-danger);
  color: var(--st-accent-text);
}
.st-btn--on-shell.st-btn--danger:hover:not(:disabled) {
  background: var(--st-danger);
}

.st-btn__icon {
  font-size: 0.9em;
}
.st-btn__spinner {
  font-size: 0.55em;
  animation: st-btn-pulse 0.9s ease-in-out infinite;
}
@keyframes st-btn-pulse {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}
</style>
